import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import CardTopBar from './CardTopBar';
import PChrome from './full/PChrome';
import FullProfileBody from './full/FullProfileBody';
import QuickActionsBar from '../modal/QuickActionsBar';
import { useProfileInsights } from './full/useProfileInsights';
import { usePlayerData } from '../hooks';
import { BOTTOM_NAV_HEIGHT } from '../../../../../services/mobileNavTheme';
import { leaguesAPI } from '../../../../../services/apiService';
import { formatApiError } from '../../../../../services/errorUtils';
import { dynastyTheme, withAlpha } from '../../../../../services/colorService';
// DD-MODAL-0723: tell the modal's ownership-bearing session cache the world changed.
import { invalidatePlayer } from '../hooks/playerDataCache';

const C = dynastyTheme.tokens.colors;

/**
 * MobilePlayerCard — full-screen player profile (the analytics suite behind the
 * modal's "Full ↑"). Unit 2b rebuild: PChrome (identity + Profile/Analytics
 * toggle + VitalStrip + ONE mode-driven tab row) over a scrollable body that
 * renders the active screen.
 *
 * Analytics mode → ATabs (Summary·Heatmap·Rankings·Impact·Trends, net-new).
 * Profile mode → FTabs (Stats·Game Logs·Scouting·Info·News·Financials, the
 *   reused modal tab components). Default landing: Analytics → Summary.
 *
 * Open/close contract preserved (playerId·leagueId·isOpen·onClose·onCollapse·
 * initialTab) so every page's existing onExpandToCard/isPlayerCardOpen wiring is
 * untouched.
 */
const MobilePlayerCard = ({ playerId, leagueId, isOpen, onClose, onCollapse, initialTab }) => {
  const [mode, setMode] = useState('analytics');
  const [atab, setAtab] = useState('sum');
  const [ftab, setFtab] = useState('info'); // Info first, like the modal
  const [isAnimating, setIsAnimating] = useState(false);

  const contentRef = useRef(null);
  const pullDownStartY = useRef(null);
  const pullDownDistance = useRef(0);
  const cardRef = useRef(null);

  const { data, loading, error } = usePlayerData(playerId, leagueId, isOpen);

  // v3 projection-insight + team surfaces (IL risk / anomaly / comparables /
  // fantasy-impact) — real endpoints, status-safe (null → honest-empty block).
  const isPitcher = ['P', 'SP', 'RP'].includes(data?.info?.position);
  const owningTeamId = data?.league_context?.team?.team_id;
  const isOwned = data?.league_context?.status === 'owned';
  const insights = useProfileInsights(playerId, { isPitcher, leagueId, owningTeamId, isOwned });

  // Mode toggle swaps the single tab row (never two stacked).
  const handleModeChange = (m) => {
    setMode(m);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };
  // PChrome routes (mode, tabKey) on any tab click.
  const handleTabChange = (m, key) => {
    if (m === 'analytics') setAtab(key); else setFtab(key);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  // Pull-down-to-close (preserved from the prior card).
  const handleTouchStart = (e) => {
    if (contentRef.current?.scrollTop === 0) {
      pullDownStartY.current = e.touches[0].clientY;
      pullDownDistance.current = 0;
    }
  };
  const handleTouchMove = (e) => {
    if (pullDownStartY.current !== null && contentRef.current?.scrollTop === 0) {
      const deltaY = e.touches[0].clientY - pullDownStartY.current;
      if (deltaY > 0) {
        pullDownDistance.current = deltaY;
        if (cardRef.current) {
          const ty = Math.min(deltaY, 300);
          cardRef.current.style.transform = `translateY(${ty}px)`;
          cardRef.current.style.opacity = `${1 - (ty / 300) * 0.4}`;
        }
      }
    }
  };
  const handleTouchEnd = () => {
    if (pullDownDistance.current > 120) {
      handleClose();
    } else if (cardRef.current) {
      cardRef.current.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
      cardRef.current.style.transform = 'translateY(0)';
      cardRef.current.style.opacity = '1';
      setTimeout(() => { if (cardRef.current) cardRef.current.style.transition = ''; }, 200);
    }
    pullDownStartY.current = null;
    pullDownDistance.current = 0;
  };

  const handleBack = () => {
    if (onCollapse) {
      setIsAnimating(true);
      setTimeout(() => { onCollapse(); setIsAnimating(false); }, 300);
    } else {
      handleClose();
    }
  };
  const handleClose = () => {
    if (onClose) {
      setIsAnimating(true);
      setTimeout(() => { onClose(); setIsAnimating(false); }, 300);
    }
  };

  // Action-bar handlers (same wiring as the modal). The 4th cell is "Return ↓"
  // (collapse to the modal) instead of "Full ↑" since we're already full.
  const handleDrop = async () => {
    const leaguePlayerId = data?.ids?.league_player;
    if (!leaguePlayerId) { alert('Error: Player ID not found'); return; }
    try {
      const res = await leaguesAPI.dropPlayerFromTeam(leagueId, leaguePlayerId);
      invalidatePlayer(leagueId, data?.ids?.mlb);
      if (res.success) { onClose(); window.dispatchEvent(new Event('roster-updated')); }
      else alert(res.message || 'Failed to drop player');
    } catch (err) {
      // DD-NIMMO-422-FE: surface the server's message (DL-residency lock, etc.)
      alert(formatApiError(err, 'Failed to drop player'));
    }
  };
  const handleAdd = (pId) => { console.log('Add player:', pId); };
  const handleTrade = (pId) => { console.log('Trade player:', pId); };

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [isOpen]);

  // Reset to the default landing (Analytics → Summary) on close.
  useEffect(() => {
    if (!isOpen) {
      setMode('analytics');
      setAtab('sum');
      setFtab('info');
    }
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  return (
    <div
      style={styles.backdrop}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        ref={cardRef}
        data-card
        className="mpc-card"
        style={{ ...styles.card, ...(isAnimating ? styles.cardAnimating : {}) }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Controls-only top bar (name renders once in PChrome). */}
        <CardTopBar onBack={handleBack} onClose={handleClose} />

        {/* Shared chrome: identity + Profile/Analytics toggle + VitalStrip + ONE tab row */}
        {data && (
          <PChrome
            player={data}
            mode={mode}
            onModeChange={handleModeChange}
            atab={atab}
            ftab={ftab}
            onTabChange={handleTabChange}
          />
        )}

        {/* Scrollable screen body */}
        <div
          ref={contentRef}
          className="mpc-content"
          style={styles.content}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {loading ? <StateBox text="Loading player data..." spinner />
            : error ? <StateBox text="Failed to load player data" sub={error?.message} />
            : !data ? <StateBox text="No player data available" />
            : (
              <FullProfileBody
                mode={mode}
                atab={atab}
                ftab={ftab}
                player={data}
                playerId={playerId}
                leagueId={leagueId}
                insights={insights}
              />
            )}
        </div>

        {/* Action bar — fixed bottom sibling (flexShrink:0). .mpc-content (flex:1)
            ends ABOVE it, so the Game Logs table clips above the buttons. 8px gap
            below the bar to the card edge (card already ends above the nav). */}
        {data && (
          <div style={styles.actionBarWrap}>
            <QuickActionsBar
              playerId={playerId}
              onAdd={handleAdd}
              onDrop={handleDrop}
              onTrade={handleTrade}
              onViewFull={handleBack}
              viewFullLabel="Return"
              viewFullVariant="down"
              isOnRoster={data.league_context?.status === 'owned'}
              showActions={!!leagueId}
              playerName={data.info?.full_name}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const StateBox = ({ text, sub, spinner }) => (
  <div style={styles.stateContainer}>
    {spinner && <div style={styles.spinner} />}
    <div style={styles.stateText}>{text}</div>
    {sub && <div style={styles.stateSubtext}>{sub}</div>}
  </div>
);
StateBox.propTypes = { text: PropTypes.string, sub: PropTypes.string, spinner: PropTypes.bool };

MobilePlayerCard.propTypes = {
  playerId: PropTypes.string.isRequired,
  leagueId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCollapse: PropTypes.func,
  initialTab: PropTypes.string,
};
MobilePlayerCard.defaultProps = { onCollapse: null, initialTab: 'Overview' };

const styles = {
  backdrop: {
    // Backdrop ends ABOVE the fixed BottomNav so the nav stays fully visible
    // (undimmed) below the full-profile card; flex-end then anchors the card at
    // the backdrop's bottom = just above the nav.
    position: 'fixed', top: 0, left: 0, right: 0, bottom: BOTTOM_NAV_HEIGHT,
    background: withAlpha(C.black, 0.6), zIndex: 1050,
    display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
  },
  card: {
    // Height comes from the .mpc-card CSS class (100dvh with a 100vh fallback) —
    // inline styles can't hold the duplicate-property fallback. dvh = the actual
    // VISIBLE viewport (accounts for mobile browser chrome), so the card is
    // exactly the visible screen and only the inner .mpc-content body scrolls.
    position: 'relative', width: '100%',
    background: C.ground.screen, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    animation: 'slideUpFull 0.3s ease-out',
    transform: 'translateY(0)', transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
  },
  cardAnimating: { animation: 'slideDownFull 0.3s ease-out' },
  content: {
    flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch',
    minHeight: 0, scrollbarWidth: 'none', msOverflowStyle: 'none',
    // Same 4px side inset as the modal's bodyInset — tab content never bleeds to
    // the screen edge (parity with MobilePlayerModal.styles.bodyInset). Also a
    // flex column so a full-height tab (Game Logs) can fill to the bottom.
    paddingLeft: '4px', paddingRight: '4px',
    display: 'flex', flexDirection: 'column',
    // 8px gap so the content scroller ends ABOVE QuickActionsBar (no overlay).
    marginBottom: '8px',
  },
  // Fixed bottom action bar — never flexes, always reserves its height so
  // .mpc-content ends above it. 8px below the bar to the card edge (the card
  // already ends above the nav) for clean separation between buttons/edge/nav.
  actionBarWrap: {
    flexShrink: 0,
    paddingBottom: '8px',
    background: C.ground.screen,
  },
  stateContainer: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    minHeight: '300px', padding: '40px 20px',
  },
  spinner: {
    width: 40, height: 40, border: `4px solid ${C.surface[855]}`, borderTop: `4px solid ${C.primary}`,
    borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: 16,
  },
  stateText: { fontSize: 15, fontWeight: 600, color: C.white, marginBottom: 8 },
  stateSubtext: { fontSize: 12, color: C.mutedLight },
};

if (typeof window !== 'undefined' && !document.getElementById('mpc-anim')) {
  const s = document.createElement('style');
  s.id = 'mpc-anim';
  s.textContent = `
    @keyframes slideUpFull { from { transform: translateY(100%); opacity: 0.8; } to { transform: translateY(0); opacity: 1; } }
    @keyframes slideDownFull { from { transform: translateY(0); opacity: 1; } to { transform: translateY(100%); opacity: 0.8; } }
    @keyframes spin { to { transform: rotate(360deg); } }
    .mpc-content::-webkit-scrollbar { display: none; }
    /* Card = the visible viewport MINUS the fixed BottomNav, so the card ends
       ABOVE the nav (nav fully visible below) and only .mpc-content scrolls. 100vh
       fallback first, 100dvh wins where supported (real visible height). */
    .mpc-card {
      height: calc(100vh - ${BOTTOM_NAV_HEIGHT}); height: calc(100dvh - ${BOTTOM_NAV_HEIGHT});
      max-height: calc(100vh - ${BOTTOM_NAV_HEIGHT}); max-height: calc(100dvh - ${BOTTOM_NAV_HEIGHT});
    }
  `;
  document.head.appendChild(s);
}

export default MobilePlayerCard;
